/**
 * The challan figure on the 3B screen (C-08, FR-E23), read without rendering.
 *
 * The service works the figure out (`returns/threeb_return.py::challan`): the cash per major
 * head by PMT-06 minor head after the Rule 88A set-off, in whole rupees. This reads it into rows
 * and sentences. **A minor head that is not computed stays words, never ₹0.00**: interest while
 * the practitioner's basis is unverified, a late fee the calendar could not give — and then the
 * total says it leaves them out.
 *
 * Also the GSTR-3B JSON's holds, in words (C-09): which cells keep the JSON from being written.
 */

import { type Gstr3bChallan, type Gstr3bReturnView, periods } from './api'
import type { GeneratedPaper } from './workingPaper'

export const MAJOR_HEADS = [
  ['igst', 'IGST'],
  ['cgst', 'CGST'],
  ['sgst', 'SGST'],
  ['cess', 'Cess'],
] as const

export const MINOR_HEADS = [
  ['tax', 'Tax'],
  ['interest', 'Interest'],
  ['penalty', 'Penalty'],
  ['fee', 'Fee'],
  ['others', 'Others'],
] as const

export type MinorKey = (typeof MINOR_HEADS)[number][0]

export interface ChallanRow {
  head: string
  /** Paise per minor head, or `null` where the figure is not computed. */
  amounts: Record<MinorKey, number | null>
  total: number
}

export function challanRows(challan: Gstr3bChallan | undefined): ChallanRow[] {
  if (!challan) return []
  return MAJOR_HEADS.map(([key, words]) => {
    const row = challan.by_head[key]
    return {
      head: words,
      amounts: {
        tax: row?.tax ?? 0,
        interest: row?.interest ?? null,
        penalty: row?.penalty ?? 0,
        fee: row?.fee ?? null,
        others: row?.others ?? 0,
      },
      total: row?.total ?? 0,
    }
  })
}

/** What the total leaves out, one sentence per minor head, in the service's words. */
export function challanLeftOut(challan: Gstr3bChallan | undefined): string[] {
  if (!challan || challan.complete) return []
  const words: Record<string, string> = { interest: 'Interest', fee: 'The late fee' }
  return Object.entries(challan.stated).map(
    ([minor, why]) => `${words[minor] ?? minor} is not in this total: ${why}`,
  )
}

/** The cells holding the GSTR-3B JSON, as "4(B)(1)" with the reason. Empty when nothing holds. */
export function jsonHolds(view: Gstr3bReturnView | undefined): { cell: string; why: string }[] {
  return Object.entries(view?.holds ?? {}).map(([cell, why]) => ({ cell: cellWords(cell), why }))
}

/** The cells whose service key carries words, as the form names them. */
const CELL_WORDS: Record<string, string> = {
  '3.2(unregistered)': '3.2 to unregistered persons',
  '3.2(composition)': '3.2 to composition taxable persons',
  '3.2(uin)': '3.2 to UIN holders',
  '5(exempt)': '5 exempt and nil-rated inward supplies',
  '5(non_gst)': '5 non-GST inward supplies',
  '5.1(interest)': '5.1 interest',
  '5.1(late_fee)': '5.1 late fee',
}

/** `4B(1)` → `4(B)(1)`, `5.1(late_fee)` → `5.1 late fee` — the form's numbering, not the key. */
export function cellWords(cell: string): string {
  const four = /^4([A-D])(\(\d\))?$/.exec(cell)
  if (four) return `4(${four[1]})${four[2] ?? ''}`
  return CELL_WORDS[cell] ?? cell
}

/**
 * Make the challan working and fetch its bytes — the same period both times, and the artifact
 * the generate call just returned (the wiring `workingPaper.ts` is held to).
 */
export async function generateChallanWorking(periodId: string): Promise<GeneratedPaper> {
  const artifact = (await periods.generateGstr3bChallan(periodId)).data
  const file = await periods.artifactFile(periodId, artifact.id)
  return { artifact, file }
}
